import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { adminListDisputes } from "@/lib/orders-admin.functions";
import {
  disputeReasonLabel,
  disputeStatusLabel,
  disputeStatusTone,
  dueState,
  levelLabel,
  resolutionLabel,
  stageLabel,
  stageSpec,
} from "@/lib/disputes";
import { kr } from "@/lib/orders";

export const Route = createFileRoute("/_authenticated/admin/tvister")({
  component: AdminDisputes,
  head: () => ({
    meta: [{ title: "Tvister – administration" }, { name: "robots", content: "noindex" }],
  }),
});

const FILTERS = [
  { value: "open", label: "Öppna" },
  { value: "resolved", label: "Avgjorda" },
  { value: "withdrawn", label: "Tillbakadragna" },
  { value: "all", label: "Alla" },
] as const;

function AdminDisputes() {
  const list = useServerFn(adminListDisputes);
  const [status, setStatus] = useState<(typeof FILTERS)[number]["value"]>("open");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-disputes", status],
    queryFn: () => list({ data: { status } }),
    refetchInterval: 60_000,
  });

  const rows = (data?.rows ?? []) as any[];
  const byStage = (data?.byStage ?? []) as { stage: string; count: number }[];

  return (
    <AdminShell title="Tvister">
      <p className="mt-2 text-muted-foreground">
        Här hamnar alla anmälda problem. Betalningen ligger kvar hos oss tills du fattar beslut.
      </p>

      <div className="mt-4 grid gap-2 sm:grid-cols-5">
        <div className="rounded-lg border border-border bg-card p-3">
          <p className="text-xs text-muted-foreground">Öppna</p>
          <p className="text-xl font-semibold">{data?.open ?? 0}</p>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50/60 p-3">
          <p className="text-xs text-red-700">Över tiden</p>
          <p className="text-xl font-semibold text-red-700">{data?.overdue ?? 0}</p>
        </div>
        {byStage.map((s) => (
          <div key={s.stage} className="rounded-lg border border-border bg-card p-3">
            <p className="text-xs text-muted-foreground">{stageLabel(s.stage)}</p>
            <p className="text-xl font-semibold">{s.count}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <Button
            key={f.value}
            size="sm"
            variant={status === f.value ? "default" : "outline"}
            onClick={() => setStatus(f.value)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {isLoading && <p className="mt-6 text-muted-foreground">Laddar…</p>}
      {!isLoading && rows.length === 0 && (
        <p className="mt-6 text-muted-foreground">Inga tvister här just nu.</p>
      )}

      <ul className="mt-6 space-y-3">
        {rows.map((d) => (
          <li key={d.id} className="rounded-xl border border-border bg-card p-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${disputeStatusTone(d.status)}`}>
                {disputeStatusLabel(d.status)}
              </span>
              <span className="font-semibold">#{d.orders?.order_no ?? "–"}</span>
              <span className="truncate">{d.orders?.ad_title ?? "Okänd annons"}</span>
              {d.orders?.is_simulated && (
                <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-semibold text-amber-600">
                  Övning
                </span>
              )}
            </div>
            {d.status === "open" && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-full bg-primary/10 px-2 py-0.5 font-semibold text-primary">
                  {stageLabel(d.stage)}
                </span>
                <span className={`rounded-full px-2 py-0.5 font-medium ${dueState(d.stage_due_at).tone}`}>
                  {dueState(d.stage_due_at).label}
                </span>
                <span className="text-muted-foreground">
                  {levelLabel(d.escalation_level)} · {stageSpec(d.stage).owner}
                </span>
              </div>
            )}
            <p className="mt-2 text-sm text-muted-foreground">
              {disputeReasonLabel(d.reason)} · anmält av {d.opened_by === "buyer" ? "köparen" : "säljaren"} ·{" "}
              {new Date(d.created_at).toLocaleString("sv-SE")} · {kr(d.orders?.total_amount ?? 0)}
            </p>
            <p className="mt-1 line-clamp-2 text-sm">{d.description}</p>
            {d.status === "resolved" && (
              <p className="mt-1 text-sm text-muted-foreground">
                Beslut: {resolutionLabel(d.resolution)}
                {Number(d.refund_amount ?? 0) > 0 && ` · ${kr(d.refund_amount)} tillbaka`}
              </p>
            )}
            <Link
              to="/admin/tvister/$id"
              params={{ id: d.id }}
              className="mt-3 inline-flex rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
            >
              Öppna ärendet
            </Link>
          </li>
        ))}
      </ul>
    </AdminShell>
  );
}
